import {Icon, Show, Space, ThemeIconKey, VStack} from 'platform/foundation';
import {cva} from 'platform/shared';

import {useId} from 'react';

import {suffixTestId, TestIdProps} from 'shared/web';

import {FormControlProps} from '../../types/FormControlProps';
import {HelperText} from '../HelperText/HelperText';
import {IconButton} from '../IconButton/IconButton';
import {PlatformInputElement} from '../TextInput/components/PlatformInputElement';
import {useTranslationContext} from '../TranslationProvider/TranslationContext';

export interface SearchProps extends TestIdProps, FormControlProps<string | null> {
  icon?: ThemeIconKey;
  highlightOnSearch?: boolean;
  isAutoFocused?: boolean;
  placeholder?: string;
  onClear?: () => void;
}

const searchVariants = cva(
  'flex h-8 max-h-8 min-h-0 w-full items-center overflow-hidden rounded-[var(--radius-small)] border border-transparent bg-[var(--color-palettes-neutral-20-100)] px-2 text-[var(--color-primary)] focus-within:border-[var(--color-severity-informational)] focus-within:bg-[var(--color-white)] focus-within:[box-shadow:var(--shadow-active)]',
  {
    variants: {
      isDisabled: {
        true: 'cursor-not-allowed opacity-40 shadow-none',
        false: 'hover:border-[var(--color-severity-informational)]',
      },
      isHighlighted: {
        true: 'bg-[var(--color-palettes-blue-20-100)]',
        false: '',
      },
      isInvalid: {
        true: 'border-[var(--color-severity-danger)]',
        false: '',
      },
    },
    defaultVariants: {
      isDisabled: false,
      isHighlighted: false,
      isInvalid: false,
    },
  }
);

export function Search(props: SearchProps) {
  const t = useTranslationContext();
  const id = useId();

  const isInvalid = props.isInvalid || !!props.errorMessage;

  const onClearClick = () => {
    props.onChange?.(null);
    props.onClear?.();
  };

  return (
    <VStack>
      <div
        className={searchVariants({
          isDisabled: !!props.isDisabled,
          isHighlighted: !!props.highlightOnSearch && !!props.value,
          isInvalid,
        })}
      >
        <Icon value={props.icon ?? 'action/search'} size={4} />
        <Space horizontal={2} />
        <PlatformInputElement
          id={id}
          isInvalid={isInvalid}
          isDisabled={!!props.isDisabled}
          placeholder={props.placeholder ?? t('general.labels.searchPlaceholder')}
          onChange={props.onChange}
          value={props.value}
          onFocus={props.onFocus}
          onBlur={props.onBlur}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
            }

            props.onKeyDown?.(event);
          }}
          isAutoFocused={props.isAutoFocused}
          autoComplete="off"
          data-testid={suffixTestId('search', props)}
        />
        <Space horizontal={2} />
        <Show when={Boolean(props.value)}>
          <IconButton
            size="small"
            icon="navigation/close"
            onClick={onClearClick}
            tabIndex={-1}
            data-testid={suffixTestId('searchClearButton', props)}
          />
        </Show>
        <Show when={!props.value}>
          <Space horizontal={6} />
        </Show>
      </div>
      <Show when={props.errorMessage ?? props.helperText}>
        <HelperText
          errorMessage={props.errorMessage}
          helperText={props.helperText}
          data-testid={suffixTestId('helper', props)}
        />
      </Show>
    </VStack>
  );
}
